import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../../constants/constants";

interface Question {
  id: number;
  text: string;
  explanation: string;
  points: number;
  type: string;
}

interface Evaluation {
  id: string;
  title: string;
  description: string;
  coursId: number;
  timeLimit: number;
  passingScore: number;
  maxAttempts: number;
  isActive: boolean;
  isUnlocked: boolean;
  isCompleted: boolean;
  showCorrectAnswers: boolean;
  shuffleQuestions: boolean;
  shuffleAnswers: boolean;
  questions: Question[];
  createdAt: string;
  updatedAt: string;
}

const EtudiantEvaluation = () => {
  // Données de progression d'apprentissage
  const dernierAcces = "12/03/2023";
  const scoreGlobal = 0; // Score sur 5 formations

  // État pour les évaluations dynamiques
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchQuizzes = async () => {
      setLoading(true);
      setError(null);
      const accessToken = localStorage.getItem("accessToken");
      try {
        const response = await fetch(API_ENDPOINTS.QUIZZ.GET_ALL_QUIZZES, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!response.ok)
          throw new Error("Erreur lors du chargement des quizzes");
        const data = await response.json();
        setEvaluations(data);
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Erreur inconnue");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchQuizzes();
  }, []);

  // État pour la gestion des onglets
  const [activeTab, setActiveTab] = useState("a-faire");

  // Filtrer les quizzes selon leur statut de complétion
  const pendingQuizzes = evaluations.filter((quiz) => !quiz.isCompleted);
  const completedQuizzes = evaluations.filter((quiz) => quiz.isCompleted);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        Évaluation et QUIZZ
      </h1>

      {/* Carte de progression */}
      <div className="border border-green-200 rounded-lg p-6 mb-8 flex flex-col md:flex-row justify-between items-start md:items-center max-w-3xl">
        <div>
          <h2 className="text-lg font-semibold">Votre apprentissage global</h2>
          <p className="text-sm text-gray-600">
            Dernier accès le {dernierAcces}
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex items-center">
          <div className="bg-white border border-gray-200 rounded-full h-16 w-16 flex items-center justify-center">
            <span className="text-lg font-bold">{scoreGlobal}%</span>
          </div>
          <div className="ml-3">
            <p className="text-sm text-gray-600">0 sur 5 formations</p>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="mb-8">
        <div className="border-b border-gray-200 flex space-x-1 p-0 mb-6">
          <button
            className={`px-4 py-2 ${
              activeTab === "a-faire"
                ? "border border-orange-500 text-orange-500 font-medium"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("a-faire")}
          >
            À FAIRE
          </button>
          <button
            className={`px-4 py-2 ${
              activeTab === "termines"
                ? "border border-orange-500 text-orange-500 font-medium"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("termines")}
          >
            TERMINÉS
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">Chargement...</div>
        ) : error ? (
          <div className="text-red-500 text-center py-8">{error}</div>
        ) : (
          <>
            {activeTab === "a-faire" && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {pendingQuizzes.length === 0 ? (
                  <div className="col-span-full text-center py-8">
                    Vous avez terminé tous les quizzes disponibles.
                  </div>
                ) : (
                  pendingQuizzes.map((evaluation) => (
                    <div
                      key={evaluation.id}
                      className="bg-white rounded-lg overflow-hidden shadow"
                    >
                      <div className="h-48 overflow-hidden bg-gray-200 flex items-center justify-center">
                        <div className="text-gray-500 text-xl">
                          Quiz: {evaluation.title}
                        </div>
                      </div>
                      <div className="p-4">
                        <h2 className="text-lg font-bold mb-2">Évaluation</h2>
                        <p className="text-sm text-gray-600 mb-2">
                          Temps: {evaluation.timeLimit} min
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                          Score requis: {evaluation.passingScore}%
                        </p>
                        <Link
                          to={`/etudiant/formation/${evaluation.coursId}/quiz/${evaluation.id}`}
                          className={`block text-white text-center py-2 px-4 rounded-md transition-colors ${
                            evaluation.isUnlocked
                              ? "bg-green-600 hover:bg-green-700"
                              : "bg-gray-400 cursor-not-allowed"
                          }`}
                          onClick={(e) =>
                            !evaluation.isUnlocked && e.preventDefault()
                          }
                        >
                          {evaluation.isUnlocked ? "Commencer" : "Verrouillé"}
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === "termines" && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {completedQuizzes.length === 0 ? (
                  <div className="col-span-full text-center py-8">
                    Vous n'avez pas encore terminé de quiz.
                  </div>
                ) : (
                  completedQuizzes.map((evaluation) => (
                    <div
                      key={evaluation.id}
                      className="bg-white rounded-lg overflow-hidden shadow"
                    >
                      <div className="h-48 overflow-hidden bg-gray-200 flex items-center justify-center">
                        <div className="text-gray-500 text-xl">
                          Quiz: {evaluation.title}
                        </div>
                      </div>
                      <div className="p-4">
                        <h2 className="text-lg font-bold mb-2">Évaluation</h2>
                        <p className="text-sm text-gray-600 mb-2">
                          Temps: {evaluation.timeLimit} min
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                          Score requis: {evaluation.passingScore}%
                        </p>
                        <Link
                          to={`/etudiant/formation/${evaluation.coursId}/quiz/${evaluation.id}`}
                          className="block bg-blue-600 text-white text-center py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Revoir
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EtudiantEvaluation;
